import type { CaseStudy } from '@portfolio/shared';
import type { JSX } from 'react';
import { Link } from 'react-router';

import { Chip } from '@/components/ui/Chip';
import { ArrowRightIcon } from '@/components/ui/icons';
import { Card } from '@/components/ui/Card';

interface CaseStudyCardProps {
  caseStudy: CaseStudy;
  headingLevel?: 'h3' | 'h4';
}

/**
 * The whole card is a link target via a stretched anchor, so pointer users get
 * a large hit area while keyboard users still tab to exactly one control.
 */
export function CaseStudyCard({
  caseStudy,
  headingLevel: Heading = 'h3',
}: CaseStudyCardProps): JSX.Element {
  return (
    <Card as="li" interactive className="relative flex flex-col">
      <div className="flex flex-wrap items-center gap-2 text-xs text-subtle">
        <Chip tone="accent">{caseStudy.domain}</Chip>
        <span>{caseStudy.organization}</span>
        <span aria-hidden="true">·</span>
        <time dateTime={caseStudy.period.start}>{caseStudy.period.label}</time>
      </div>

      <Heading className="mt-4 text-xl">
        <Link
          to={`/case-studies/${caseStudy.slug}`}
          className="text-text no-underline after:absolute after:inset-0 after:content-[''] hover:underline"
        >
          {caseStudy.title}
        </Link>
      </Heading>

      <p className="mt-3 flex-1 text-sm text-muted">{caseStudy.cardSummary}</p>

      <ul aria-label={`${caseStudy.title} focus areas`} className="mt-5 flex flex-wrap gap-2">
        {caseStudy.focus.slice(0, 3).map((item) => (
          <li key={item}>
            <Chip>{item}</Chip>
          </li>
        ))}
      </ul>

      <p
        aria-hidden="true"
        className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-accent"
      >
        Read the case study
        <ArrowRightIcon width={16} height={16} />
      </p>
    </Card>
  );
}
